import { Row, Col } from "react-bootstrap";
import { GenericTooltip } from "../form/GenericTooltip";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { GenericFormFields } from "../form/GenericFormFields";
import { DetailsCollapse } from "../detailsCollapse/DetailsCollapse";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DataObjectIcon from '@mui/icons-material/DataObject';
import JSONPretty from "../../jsonPrettyOverride/JSONPretty";
import classes from "../../../pages/IntegrationWizard.module.scss"
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { DDD,
        mergeDDDIAndUI,
        getInvoiceValuesFromMergedDDDIAndUI, 
        E2Data} from "../../../lib/ddd/ddd";
import IntegrationInvoiceSpinner from "./IntegrationInvoiceSpinner";
import { useQuery } from "@tanstack/react-query";
import Ajv from "ajv";
import {invoiceData} from "./testInvoiceData";
import { OrgIdContext } from "../../../store/orgId_context";
import Button from "../../UI/Button";
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import MainInvoice from "../invoices/MainInvoice";
import GenericModal from "../../UI/GenericModal";
import { Tabs, Tab, Box, Tooltip as MuiTooltip } from "@mui/material";
import KeysDescription from "../complexity/KeysDescription";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const ajv = new Ajv({allErrors: true});
const e2Data = new E2Data(apiRequestAddress);
e2Data.entity="IL_EU_CacheByCountry";
e2Data.format="Full";

const IntegrationInvoice = forwardRef((props, ref)=>{
    const {sessionInfo} = useContext(OrgIdContext);
    const {onJsonChanged, invoiceId, onLoaded, json, flag, onFlagChanged} = props;
    const { isMaintainer } = useContext(OrgIdContext);
    const [formValidation, setFormValidation] = useState(false);
    const [formErrorsText, setFormErrorsText] = useState();
    const [lastFieldChange, setLastFieldChange] = useState(null);
    const [lastFieldChangeIndex, setLastFieldChangeIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [mergedJson, setMergedJson] = useState();
    const [editorJson, setEditorJson] = useState();
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
    const [mandatoryOnly, setMandatoryOnly] = useState(true);
    const [invoiceModal, setInvoiceModal] = useState();
    const { integrationDevice} = useContext(OrgIdContext);
    const [mode, setMode] = useState("Payload"); //Payload,Form
    const [cacheData, setCacheData] = useState([]);
    
    const {_details, ...mainForm} = mergedJson ? mergedJson : {};
    useImperativeHandle(ref, () =>{
        return{
            validate(){
                return validateForm();
            }
        }
    },[formValidation, editorJson]);

    const {isPending : isMinimalPending, data : minimalData} = useQuery({
        queryKey: ['APP_GetNewDDDIAndUIMerged', "Normal", "IncludeInfo", "EditableOnly", integrationDevice],
        queryFn: () => DDD.WithConnectionKey(apiRequestAddress, integrationDevice).APP_GetNewDDDIAndUIMerged({Complexity: "Normal", IncludeInfo : true}, {Complexity: "Normal", EditableOnly: true}),
        enabled : integrationDevice!==null && integrationDevice!==undefined,
        staleTime: Infinity
    })

    const {isPending : isCachePending, data : queryCacheData} = useQuery({
        queryKey: ['IL_EU_CacheByCountry', e2Data.GetParamsObject()],
        queryFn: () => e2Data.GetData(),
        staleTime: Infinity
    })

    useEffect(() => {
        if(!initialLoadCompleted)
            return;
        
        if(onJsonChanged)
            onJsonChanged(editorJson)
    }, [editorJson])

    useEffect(()=>{
        if(isCachePending || !queryCacheData)
            return;

        setCacheData(queryCacheData.result?.data || []);
    },[isCachePending,queryCacheData]);

    useEffect(() => {
        if(isMinimalPending || !minimalData)
            return;
        
        setIsLoading(false);
        const {DDDI_GetNew, UI_GetNew} = minimalData;
        let deepClone = mergeDDDIAndUI(DDDI_GetNew.Invoice, UI_GetNew.Invoice);

        if(json)
            copyValues(deepClone,json);
        setMergedJson(deepClone);

        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(deepClone);

        if(invoiceId)
            setEditorJson({"Object" : {"Id" : invoiceId, ...invoiceWithValues}, "Complexity" : "Normal"});
        else
            setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal"});
        
        setInitialLoadCompleted(true);
        if(onLoaded) onLoaded();

    }, [isMinimalPending, minimalData]);

    const onFlagClicked = (data) =>{
        if(onFlagChanged) onFlagChanged(data.Country__CountryCodeISOC2?.toLowerCase());

        //setSelectedFlag(data.Country__CountryCodeISOC2?.toLowerCase());

        let DDDI_GetNew = JSON.parse(data["DDDI_GetNew"]);
        let UI_GetNew = JSON.parse(data["UI_GetNew"]);
        let deepClone = mergeDDDIAndUI(DDDI_GetNew.Result.Invoice, UI_GetNew.Result.Invoice);

        //copyValues(deepClone, {Object : minimalData?.DDDI_GetNew?.Invoice?.Invoice});
        if(minimalData?.DDDI_GetNew?.Invoice?.Invoice?.DeviceId)
            deepClone.DeviceId.Value = minimalData?.DDDI_GetNew?.Invoice?.Invoice?.DeviceId;
        /*if(json)
            copyValues(deepClone,json);*/

        setMergedJson(deepClone);
        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(deepClone);
        if(invoiceId)
            setEditorJson({"Object" : {"Id" : invoiceId, ...invoiceWithValues}, "Complexity" : "Normal"});
        else
            setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal"});

        reloadForm();
    }

    const copyValues = (merged, json) =>{
        if(!json || !json.hasOwnProperty("Object"))
            return;
        const saved = json.Object;
        for(const field in saved){
            if(field === "_details" || field==="Id" || !merged[field])
                continue;
            merged[field].Value=saved[field];
        }
        for(const field in saved._details){
            copyArrays(merged._details[field], saved._details[field], field)
        }
    }

    const copyArrays = (mergeArr, jsonArr, detail)=>{
        let missing = jsonArr.length-mergeArr.length;
        for(let i=0;i<missing;i++){
            mergeArr.push(JSON.parse(JSON.stringify(mergeArr[0])));
        }
        for(let i=0;i<jsonArr.length;i++){
            copyObjectValue(mergeArr[i],jsonArr[i]);
        }
    }

    const copyObjectValue = (mergeObj, jsonObj)=>{
        for(const field in jsonObj){
            if(!mergeObj[field])
                continue;
            mergeObj[field].Value=jsonObj[field];
        }
    }

    const filterObject = (obj) => {
        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(obj);
        if(invoiceId)
            setEditorJson({"Object" : {"Id" : invoiceId, ...invoiceWithValues}, "Complexity" : "Normal"});
        else
            setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal"});
        //setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal"});
    }

    const onAddDetailClick = (detail) =>{
      if(!mergedJson["_details"][detail] || mergedJson["_details"][detail].length ===0)
        return;
      
      mergedJson["_details"][detail].push(createNewDetailObject(detail));
      setMergedJson({...mergedJson});
      filterObject(mergedJson);
    }

    const onRemoveDetailClick = (detail, index) => {
      mergedJson["_details"][detail].splice(index, 1);
      setMergedJson({...mergedJson});
      filterObject(mergedJson);
    }

    const createNewDetailObject = (detail) =>{
        let ret = JSON.parse(JSON.stringify(mergedJson["_details"][detail][0]));
        for(let fieldName in ret){
            ret[fieldName].Value = ret[fieldName].defaultValue;
        }

        return ret;
    }

    const updateValueByKey1 = (obj, key, value, detail, index) => {
        if(obj.hasOwnProperty(key)){
            obj[key].Value = value;
        }

        if(detail && obj["_details"][detail] && obj["_details"][detail][index]){
            updateValueByKey1(obj["_details"][detail][index], key, value);
        }
    }

    const handleFieldChange1 = (fieldName, value, {fieldConfig, detail, index, e}) =>{
        setLastFieldChangeIndex(isNaN(index) ? null : index);
        setLastFieldChange(fieldName);
        updateValueByKey1(mergedJson, fieldName, value, detail, index);
        setMergedJson(mergedJson);
        filterObject(mergedJson);
        setTimeout((counter, field) => {
            document.getElementById("wizardFormScroll")?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
        }, 500)
    }

    const validateForm = () =>{
        if(!formValidation)
            return true;

        const {DDDI_GetNew} = minimalData;
        const validate = ajv.compile(DDDI_GetNew.JSONSchema.properties.Invoice)
        const valid = validate(editorJson.Object)
        if (!valid){
            setFormErrorsText(validate.errors.map(item =>  {
                return(
                    <div>{item.dataPath} : {item.message}</div>
                )
            }))
        }else{
            setFormErrorsText(null);
            return true;
        }
        return false;
    }
    const getPreloadData = () =>{
        /*if(flag === "eu") return invoiceData;
        let row = cacheData.find(i => i.Country__CountryCodeISOC2?.toLowerCase()===flag);*/
        let row = cacheData.find(i => i.Country__CountryCodeISOC2?.toLowerCase()===sessionInfo?.Extended?.CountryCodeSC__CountryCode?.toLowerCase());
        try{
            const data = JSON.parse(row.DDDI_GetExisting).Result.Invoice.Invoice;
            return {Object : data};
        }catch{
            return invoiceData;
        }
    }
    const preloadFormData = () => {
        copyValues(mergedJson,getPreloadData());
        setMergedJson(mergedJson);
        let invoiceWithValues = getInvoiceValuesFromMergedDDDIAndUI(mergedJson);
        if(invoiceId)
            setEditorJson({"Object" : {"Id" : invoiceId, ...invoiceWithValues}, "Complexity" : "Normal"});
        else
            setEditorJson({"Object" : {...invoiceWithValues}, "Complexity" : "Normal"});

        setIsLoading(true);
        setTimeout(() => {setIsLoading(false);}, 1000);
    }

    const reloadForm = () =>{
        setIsLoading(true);
        setTimeout(() => {setIsLoading(false);}, 1000);
    }

    const onConfirmInvoiceModal = () =>{
        setInvoiceModal(false);
        setMode("Payload");
        reloadForm();
    }

    const onModeChange = (e, val) =>{
        setMode(val);
        if(val==="Invoice"){
            setInvoiceModal(true);
        }
    }

    


    return (<>
{!isLoading && <>
        <Row style={{backgroundColor:"white"}}>
            
        </Row>
        
        <GenericModal className="bs-modal-80" show={invoiceModal} title={"Invoice form example"} onHide={() => {setMode("Payload"); setInvoiceModal(false);}}  bodyStyle={{maxHeight:"none"}}>
            <MainInvoice fieldInfo={mergedJson} onFieldChange={handleFieldChange1} onAddDetail={onAddDetailClick} onRemoveDetail={onRemoveDetailClick} onConfirm={onConfirmInvoiceModal}/>
        </GenericModal>
        <Row className = "g-5">
            <Col className="col-md-5">
                <div className={classes["titleRows"]} >
                    <span><h3><DataObjectIcon/>Payload Object preview</h3></span>
                    <div className={classes["wizard-text"]}>This is Object preview. At this moment, we're only learning about the basic information needed for an invoice, so we're only building the Object keyword.
                        As we move on, we will add other information (Steps and ReturnDoc)
                    </div>
                    {/* <Button onClick={preloadFormData}>Preload form</Button> */}
                </div>
                {/* <div className={classes["titleRows"]}>
                    <span><h3><ReceiptIcon/>Enter invoice data</h3></span>
                    <div className={classes["wizard-text"]}>Here, you can enter your invoice information. The information you write will be reflected on the right in the preview object. Note! The amount of information your invoice requires is controlled by the "Complexity" parameter. We'll learn how to pass more information about your invoices when we talk about "Complexity".
                    </div>
                </div> */}
                {/* <div style={{display:"flex"}}>
                    <Select

                    style={{borderRadius:"10px", border:"1px solid #005683"}}
                    sx={{ padding : "0px", boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 0 }, "& .MuiSelect-select": {padding:"1px 5px"} }}
                    >
                        {cacheData.map((data, i)=>{
                            return(
                                <MenuItem value={data.Country__CountryCodeISOC2} key={i}>
                                    <img  src={`${process.env.REACT_APP_ORIGIN || window.location.origin}/Content/images/flag-icons/flags/4x3/${data.Country__CountryCodeISOC2?.toLowerCase()}.svg`} width="25"/>
                                    <span style={{paddingLeft:"1rem"}}>{data["Country__CountryNameOriginal"]}</span>
                                </MenuItem>)
                        })}
                    </Select>
                    <OverlayTrigger trigger={"click"} placement="top" overlay={
                        <Popover>
                            <div class="d-flex p-2">
                            {cacheData.map((data, i)=>{
                                return (<img  src={`${process.env.REACT_APP_ORIGIN || window.location.origin}/Content/images/flag-icons/flags/4x3/${data.Country__CountryCodeISOC2?.toLowerCase()}.svg`} width="25"/>)
                            })}
                            </div>
                        </Popover>
                    }><DynamicFormIcon/></OverlayTrigger>
                </div> */}
                <div id="preloadForm" style={{display:"flex"}}>
                    <label>Fill form options
                    <GenericTooltip tooltip={"Options to fill the form below in different way"} fieldName={"preload"} />
                    </label>
                    <Button id="dummyData" className="ms-auto" onClick={preloadFormData} tooltip="Fill the form with dummy data">
                        <OverlayTrigger placement="top" overlay={<Tooltip>{"Fill the form with dummy data"}</Tooltip>}><DynamicFormIcon/></OverlayTrigger>
                    </Button>
                    {/* <MuiTooltip placement="top" title={<div className="d-flex">
                        {cacheData.map((data, i)=>{
                            return (<img className={classes["popup-flag-btn"]} onClick={() => onFlagClicked(data)} src={`${process.env.REACT_APP_ORIGIN || window.location.origin}/Content/images/flag-icons/flags/4x3/${data.Country__CountryCodeISOC2?.toLowerCase()}.svg`} width="25"/>)
                        })}
                    </div>}>
                        <Button id="flag" className={`ms-2`}>
                            <img  src={`https://dddinvoices.com/Content/images/flag-icons/flags/4x3/${flag||"eu"}.svg`} width="25" style={{margin:"3px"}}/>
                        </Button>
                    </MuiTooltip> */}
                    {/* <Button id="invoiceInput" className={`ms-2`} onClick={() => setInvoiceModal(true)}>
                        <OverlayTrigger placement="top" overlay={<Tooltip className={"pic-tooltip"}><TooltipPic width="250" src={invoiceInput}/><div>View form as invoice</div></Tooltip>}><ViewQuiltIcon/></OverlayTrigger>
                    </Button> */}
                    
                </div>
                <Box sx={{ width: '100%',  borderRadius:"5px",  }}>
                    <Tabs value={mode} onChange={onModeChange} aria-label="icon label tabs" centered>
                        <Tab value="Payload" label="Payload" />
                        <Tab value="Form" label="Edit payload" />
                        <Tab value="Invoice" label="Edit as invoice" />
                    </Tabs>
                </Box>
                {mode === "Payload" && <>
                <div className={classes["form-wrapper"]} style={{textAlign : "left"}} id="invoiceJson">
                    <JSONPretty data={editorJson}></JSONPretty>
                </div>
                </>}
                {mode === "Form" && <><div id="enableValidation" style={{display:"flex", justifyContent:"space-between"}}>
                    <label>Enable form validation
                    <GenericTooltip tooltip={"If enabled, this validation occurs only on client side. Its purpose is only to ease your entry of information in the form below."} fieldName={"validationOffon"} />
                    </label>
                    {formErrorsText && 
                    <div style={{color:"red"}}>
                        Errors 
                        <OverlayTrigger placement="top" overlay={<Tooltip>{formErrorsText}</Tooltip>}>
                            <ErrorOutlineIcon style={{marginLeft:"0.5rem", fontSize:"1rem", color:"red"}}/>
                        </OverlayTrigger>
                    </div>}
                    <div className="form-check form-switch text-center">
                    <input className="form-check-input" style={{float:"none"}} type="checkbox" role="switch" checked={formValidation} onChange={() =>setFormValidation(val => !val)}/>
                    </div>
                </div>
                <div id="enableMandatory" style={{display:"flex", justifyContent:"space-between"}}>
                    <label>Mandatory only 
                    <GenericTooltip tooltip={"Enables only mandatory fields"} fieldName={"mandatoryOffon"} />
                    </label>
                    <div className="form-check form-switch text-center">
                        <input className="form-check-input" style={{float:"none"}} type="checkbox" role="switch" checked={mandatoryOnly} onChange={() =>setMandatoryOnly(val => !val)}/>
                    </div>
                </div>
                
                <div className={classes["form-wrapper"]} id="invoiceForm">
                    <form>
                    {mergedJson &&
                    <>
                        <GenericFormFields fieldInfo={mainForm} onFieldChange={handleFieldChange1} showFieldName={true} mandatoryOnly={mandatoryOnly}></GenericFormFields>
                    </>
                    }
                    {_details &&
                        Object.entries(_details).map(([detail, forms]) => {
                        return (
                            <DetailsCollapse detail={detail} forms={forms} opened={true} onAddClick={onAddDetailClick}>
                            {forms.map((form, index) => {
                                return (<div key={detail+index} className={classes["detail"]}>
                                {forms.length > 1 && 
                                    <div style={{textAlign:"left"}}>
                                    <i className={`${classes["detailCloseBtn"]} ti-close`} onClick={() => onRemoveDetailClick(detail, index)}></i>
                                    </div>
                                }
                                    <GenericFormFields fieldInfo={form} onFieldChange={handleFieldChange1} showFieldName={true} className={classes["detailRows"]} detail={detail} index={index} mandatoryOnly={mandatoryOnly}></GenericFormFields>
                                    
                                </div>)
                            })}
                            </DetailsCollapse>
                        )
                        })
                    }
                    </form>
                </div>
                </>}
            </Col>
            <Col className="my-auto p-0 m-0">
                    <div ><ArrowForwardIcon style={{fontSize:"2rem"}}/></div>
            </Col>
            <Col className="col-md-6 order-md-last">
                {/* <div className={classes["titleRows"]} >
                    <span><h3><DataObjectIcon/>Payload Object preview</h3></span>
                    <div className={classes["wizard-text"]}>This is Object preview. At this moment, we're only learning about the basic information needed for an invoice, so we're only building the Object keyword.
                        As we move on, we will add other information (Steps and ReturnDoc).<br/><br/><br/>
                        
                    </div>
                </div> */}
                <div className={classes["titleRows"]} >
                    <span><h3><DataObjectIcon/>Field descriptions</h3></span>
                    <div className={classes["wizard-text"]}>
                        Below you can find more about the fields required in the invoice
                    </div>
                </div>
                {/* <div className={classes["form-wrapper"]} style={{textAlign : "left"}} id="invoiceJson">
                    <JSONPretty data={editorJson} fieldName={lastFieldChange} fieldIndex={lastFieldChangeIndex} idToScroll="wizardFormScroll"></JSONPretty>
                </div> */}
                <div className={classes["form-wrapper"]} style={{textAlign : "left", maxHeight:"75vh"}} id="invoiceJson">
                {/* <ComplexityDescription complexity="Normal" connectionKey={integrationDevice}/> */}
                <KeysDescription mainData={minimalData}/>
                </div>
            </Col>
        </Row>
</>}
{isLoading && <IntegrationInvoiceSpinner/>}
</>)
})
export default IntegrationInvoice;